import React, { useRef } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { ChevronRight, ChevronLeft, ArrowUpRight } from "lucide-react";

function Story() {
  const scrollRef = useRef(null);

  const items = [
    {
      title: "خلوة الامام الطبري",
      image:
        "https://i.ibb.co/8KcrML6/465515713-18047723030506037-3586469520068209461-n.jpg",
    },
    {
      title: "خلوة قرية لانا",
      image:
        "https://i.ibb.co/7CKwStk/465750345-18047723057506037-1964922867198231648-n.jpg",
    },
    {
      title: "مشروع إنارة",
      image:
        "https://i.ibb.co/7JrT45M/465820547-18047722988506037-1525478395742935929-n.jpg",
    },
    {
      title: "إعادة الأمل",
      image:
        "https://i.ibb.co/frYGNDJ/465820639-18047723021506037-5373095694920165724-n.jpg",
    },
    {
      title: "إنارة الأمل",
      image:
        "https://i.ibb.co/4NB6r94/465852172-18047723009506037-3006684529859806868-n.jpg",
    },
    {
      title: "المساعدة بتفرق",
      image:
        "https://i.ibb.co/s1GRg1r/465886823-18047968454506037-3788874869299833563-n.jpg",
    },
    {
      title: "معاناة السودان",
      image:
        "https://i.ibb.co/2FsLt1s/466039883-18047968436506037-6288437755155844775-n.jpg",
    },
  ];

  const scroll = (direction) => {
    if (scrollRef.current) {
      const { scrollLeft, clientWidth } = scrollRef.current;
      const scrollTo =
        direction === "left" ? scrollLeft - 200 : scrollLeft + 200;
      scrollRef.current.scrollTo({ left: scrollTo, behavior: "smooth" });
    }
  };

  return (
    <div
      className="w-full bg-blue-600 py-6 md:py-8 lg:py-10 relative overflow-hidden group/section"
      dir="rtl"
    >
      {/* Decorative Background Glow */}
      <div className="absolute top-0 left-1/4 w-48 md:w-64 h-48 md:h-64 bg-blue-400/20 blur-[80px] md:blur-[100px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto px-3 sm:px-6 md:px-8 lg:px-10 relative">
        {/* Navigation Arrows (Desktop Only) */}
        <button
          onClick={() => scroll("right")}
          className="absolute -right-2 md:-right-4 top-1/2 -translate-y-1/2 z-10 p-1.5 md:p-2 bg-white/10 hover:bg-white/90 hover:text-blue-600 text-white rounded-full transition-all opacity-0 group-hover/section:opacity-100 hidden lg:flex shadow-xl backdrop-blur-md"
        >
          <ChevronRight size={20} className="md:w-6 md:h-6" />
        </button>

        <div className="flex items-center gap-4 md:gap-6 lg:gap-8">
          {/* Scrollable Container */}
          <div
            ref={scrollRef}
            className="flex flex-1 gap-3 md:gap-4 lg:gap-6 overflow-x-auto scroll-smooth pb-2 md:pb-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          >
            {items.map((item, index) => (
              <div
                key={index}
                className="group flex flex-col items-center min-w-[75px] xs:min-w-[85px] sm:min-w-[95px] md:min-w-[100px] lg:min-w-[110px] cursor-pointer"
              >
                {/* Instagram Gradient Ring */}
                <div className="relative p-[2px] md:p-[3px] rounded-full bg-gradient-to-tr from-[#f9ce34] via-[#ee2a7b] to-[#6228d7] transition-all duration-500 group-hover:rotate-[15deg] group-hover:shadow-[0_0_15px_rgba(238,42,123,0.3)] md:group-hover:shadow-[0_0_20px_rgba(238,42,123,0.4)]">
                  <div className="p-[2px] md:p-[3px] bg-blue-600 rounded-full">
                    <div className="w-[60px] h-[60px] xs:w-[70px] xs:h-[70px] sm:w-[80px] sm:h-[80px] md:w-[85px] md:h-[85px] lg:w-[95px] lg:h-[95px] rounded-full overflow-hidden grayscale-[30%] group-hover:grayscale-0 transition-all duration-500 border-2 border-white/10">
                      <LazyLoadImage
                        alt={item.title}
                        src={item.image}
                        effect="opacity"
                        className="object-cover w-full h-full scale-100 group-hover:scale-110 transition-transform duration-700"
                      />
                    </div>
                  </div>
                </div>

                {/* Title */}
                <span className="mt-2 md:mt-3 lg:mt-4 text-[11px] xs:text-[12px] sm:text-[13px] md:text-[14px] lg:text-sm font-bold text-white/90 text-center line-clamp-2 max-w-[70px] xs:max-w-[80px] sm:max-w-[95px] md:max-w-[100px] lg:max-w-[105px] group-hover:text-white transition-colors leading-tight">
                  {item.title}
                </span>
              </div>
            ))}

            {/* End Gap for mobile scrolling */}
            <div className="min-w-[1px] h-full" />
          </div>

          {/* Action Button: Glassmorphism Design */}
          <div className="hidden sm:flex flex-col items-center justify-center min-w-[100px] sm:min-w-[120px] md:min-w-[130px] lg:min-w-[140px] h-[120px] sm:h-[130px] md:h-[140px] lg:h-[160px] rounded-[1.5rem] sm:rounded-[2rem] md:rounded-[2.5rem] bg-white/5 backdrop-blur-lg hover:bg-white/15 transition-all duration-300 border border-white/10 cursor-pointer group/btn shadow-2xl overflow-hidden relative">
            <div className="absolute top-0 right-0 p-2 md:p-3 text-white/30 group-hover/btn:text-white/80 transition-colors">
              <ArrowUpRight size={16} className="md:w-5 md:h-5" />
            </div>

            <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-11 md:h-11 lg:w-12 lg:h-12 bg-white text-blue-600 rounded-xl sm:rounded-[1rem] md:rounded-2xl flex items-center justify-center transition-all duration-500 shadow-xl">
              <ChevronLeft
                size={20}
                strokeWidth={3}
                className="sm:w-6 sm:h-6 md:w-7 md:h-7 lg:w-7 lg:h-7"
              />
            </div>

            <div className="mt-2 sm:mt-3 md:mt-4 text-[11px] sm:text-xs md:text-sm font-black text-white text-center leading-tight px-2">
              عرض كافة <br />
              <span className="text-blue-200">المشاريع</span>
            </div>
          </div>

          {/* Mobile Action Button - Simplified for smallest screens */}
          <div className="flex sm:hidden flex-col items-center justify-center min-w-[70px] h-[90px] rounded-2xl bg-white/5 backdrop-blur-lg border border-white/10 cursor-pointer group/btn">
            <div className="w-7 h-7 bg-white text-blue-600 rounded-lg flex items-center justify-center">
              <ChevronLeft size={16} strokeWidth={3} />
            </div>
            <div className="mt-1.5 text-[9px] font-bold text-white text-center leading-tight">
              عرض <br />
              <span className="text-blue-200">المشاريع</span>
            </div>
          </div>
        </div>

        <button
          onClick={() => scroll("left")}
          className="absolute -left-2 md:-left-4 top-1/2 -translate-y-1/2 z-10 p-1.5 md:p-2 bg-white/10 hover:bg-white/90 hover:text-blue-600 text-white rounded-full transition-all opacity-0 group-hover/section:opacity-100 hidden lg:flex shadow-xl backdrop-blur-md"
        >
          <ChevronLeft size={20} className="md:w-6 md:h-6" />
        </button>
      </div>
    </div>
  );
}

export default Story;
